import dayjs from 'dayjs'

import type {
  MentionSegment,
  TeamMember,
  TeamMessage,
  TeamMessagesPage,
  TeamRequest,
  TeamRequestFilter,
  TeamRequestsResponse,
  TeamRequestStatus,
  TeamUser,
} from './teamChat.types'

// Same as the backend's /(?<![\w@])@([A-Za-z0-9_]+)/u: \w without the u flag is ASCII, so jo@Maria.com is no mention.
const MENTION_TOKEN = /((?<![\w@])@\w+)/g
// Hex in any case and length, or a plain rgb()/hsl() call: nothing that can break out of the style value.
const SAFE_COLOR = [
  /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i,
  /^(?:rgba?|hsla?)\([\d\s.,%/a-z]+\)$/i,
]
// The design's avatar palette (--team-avatar-1..5), picked by member id when the API sends no usable color.
const AVATAR_PALETTE_SIZE = 5
const STATUS_ORDER: Record<TeamRequestStatus, number> = { open: 0, in_progress: 1, done: 2 }
const VERBS = ['requested', 'took', 'released', 'finished']

// Broadcast flags are computed for the actor, so rebuild them for the viewer.
export const forReceiverMessage = (m: TeamMessage, meId: number): TeamMessage => ({
  ...m,
  is_mine: m.user.id === meId,
  mentions_me: m.mention_user_ids.includes(meId),
})

export const forReceiverRequest = (r: TeamRequest, meId: number): TeamRequest => ({
  ...r,
  is_mine: r.created_by.id === meId,
  owned_by_me: r.owner?.id === meId,
  mentions_me: r.mention_user_ids.includes(meId),
})

export const eventLineText = (m: TeamMessage): string =>
  [m.user.name, m.event_verb, `“${m.body}”`].filter(Boolean).join(' ')

export const mergeMessages = (current: TeamMessage[], incoming: TeamMessage[]): TeamMessage[] => {
  const byId = new Map(current.map((m) => [m.id, m]))
  incoming.forEach((m) => byId.set(m.id, m))
  return [...byId.values()].sort((a, b) => a.id - b.id)
}

// A newest page that starts after the cache ends means messages were missed: start over from it.
export const mergeNewestPage = (
  cache: TeamMessagesPage,
  incoming: TeamMessagesPage,
): TeamMessagesPage => {
  const newestCached = cache.messages.at(-1)?.id ?? 0
  const oldestIncoming = incoming.messages[0]?.id ?? 0
  if (incoming.has_more && oldestIncoming > newestCached) return incoming
  return {
    ...incoming,
    messages: mergeMessages(cache.messages, incoming.messages),
    has_more: cache.has_more,
  }
}

const belongsTo = (r: TeamRequest, filter: TeamRequestFilter): boolean =>
  filter === 'done' ? r.status === 'done' : r.status !== 'done'

const compareActive = (a: TeamRequest, b: TeamRequest): number =>
  STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || b.id - a.id

const compareDone = (a: TeamRequest, b: TeamRequest): number =>
  (b.done_at ?? '').localeCompare(a.done_at ?? '') || b.id - a.id

const COUNT_KEY = { active: 'active_count', done: 'done_count' } as const

// Requests are created active and never reopen, so only a move into done shifts a count between lists.
const otherCountDelta = (wasHere: boolean, belongs: boolean, filter: TeamRequestFilter): number => {
  if (wasHere && !belongs) return 1
  return !wasHere && belongs && filter === 'done' ? -1 : 0
}

export const applyRequestToResponse = (
  resp: TeamRequestsResponse,
  request: TeamRequest,
  filter: TeamRequestFilter,
): TeamRequestsResponse => {
  const wasHere = resp.requests.some((r) => r.id === request.id)
  const belongs = belongsTo(request, filter)
  const others = resp.requests.filter((r) => r.id !== request.id)
  const requests = (belongs ? [...others, request] : others).sort(
    filter === 'done' ? compareDone : compareActive,
  )
  const otherKey = COUNT_KEY[filter === 'done' ? 'active' : 'done']
  const otherCount = Math.max(0, resp[otherKey] + otherCountDelta(wasHere, belongs, filter))
  return { ...resp, requests, [COUNT_KEY[filter]]: requests.length, [otherKey]: otherCount }
}

const memberByHandle = (handle: string, members: TeamMember[]): TeamMember | undefined =>
  members.find((m) => m.handle.toLowerCase() === handle.toLowerCase())

export const findMentionedMembers = (text: string, members: TeamMember[]): TeamMember[] => {
  const found = (text.match(MENTION_TOKEN) ?? [])
    .map((token) => memberByHandle(token.slice(1), members))
    .filter((m): m is TeamMember => Boolean(m))
  return found.filter((m, i) => found.findIndex((x) => x.id === m.id) === i)
}

// Mirrors the backend rule for create_request, with the backend mention regex.
export const canAlsoCreateRequest = (text: string, members: TeamMember[]): boolean => {
  const asksSomeoneElse = findMentionedMembers(text, members).some((m) => !m.is_me)
  return asksSomeoneElse && text.replace(MENTION_TOKEN, '').trim().length > 0
}

export const toMentionSegments = (text: string, members: TeamMember[]): MentionSegment[] =>
  text
    .split(MENTION_TOKEN)
    .filter((part) => part !== '')
    .map((part) => {
      const found = part.startsWith('@') ? memberByHandle(part.slice(1), members) : undefined
      if (!found) return { text: part, kind: 'plain' }
      return { text: part, kind: found.is_me ? 'mentionMe' : 'mention' }
    })

const fallbackColor = (id: number): string =>
  `var(--team-avatar-${(Math.abs(Math.trunc(id)) % AVATAR_PALETTE_SIZE) + 1})`

export const safeColor = (color: string, id: number): string => {
  const value = color.trim()
  return SAFE_COLOR.some((re) => re.test(value)) ? value : fallbackColor(id)
}

const isClientError = (status: unknown): boolean =>
  typeof status === 'number' && status >= 400 && status < 500

// Network and 5xx errors carry raw axios text, so only a 4xx shows the server's message.
export const teamErrorText = (error: unknown, fallback: string): string => {
  const { status, data } = (error ?? {}) as { status?: unknown; data?: unknown }
  if (!isClientError(status)) return fallback
  const first = Array.isArray(data) ? data[0] : (data as { message?: unknown } | undefined)?.message
  return typeof first === 'string' && first ? first : fallback
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null
const isDate = (v: unknown): boolean => typeof v === 'string' && dayjs(v).isValid()
const isDateOrNull = (v: unknown): boolean => v === null || isDate(v)
const isIdList = (v: unknown): boolean => Array.isArray(v) && v.every(Number.isInteger)

const isTeamUser = (v: unknown): v is TeamUser =>
  isRecord(v) &&
  Number.isInteger(v.id) &&
  ['name', 'handle', 'role', 'color', 'initial'].every((k) => typeof v[k] === 'string')

export const isTeamMessage = (v: unknown): v is TeamMessage =>
  isRecord(v) &&
  Number.isInteger(v.id) &&
  (v.type === 'text' || v.type === 'event') &&
  typeof v.body === 'string' &&
  isTeamUser(v.user) &&
  (v.event_verb === null || VERBS.includes(v.event_verb as string)) &&
  isIdList(v.mention_user_ids) &&
  isDate(v.created_at)

export const isTeamRequest = (v: unknown): v is TeamRequest =>
  isRecord(v) &&
  Number.isInteger(v.id) &&
  typeof v.title === 'string' &&
  (v.status === 'open' || v.status === 'in_progress' || v.status === 'done') &&
  isTeamUser(v.created_by) &&
  (v.owner === null || isTeamUser(v.owner)) &&
  isIdList(v.mention_user_ids) &&
  isDate(v.created_at) &&
  isDateOrNull(v.claimed_at) &&
  isDateOrNull(v.done_at)
